import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { baseURL } from "../../../api";
import CustomAlert from "../../../component/AlertComponent/Alert";
import { toast } from "react-toastify";

const baseSections = [
  { key: "main", label: "Main" },
  { key: "details", label: "Details" },
  { key: "summary", label: "Summary" }
];

const emptySection = { tableName: "", fields: [] };

export default function ReportFieldConfigurationDialog({
  open,
  onClose,
  onSaved,
  transactionType,
  authHeaders
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const sections = useMemo(
    () => [
      ...baseSections,
      ...(configuration?.related || []).map((relation, index) => ({
        key: `related-${index}`,
        label: relation.label || relation.tableName || `Related ${index + 1}`,
        relatedIndex: index
      }))
    ],
    [configuration]
  );

  const loadConfiguration = useCallback(async () => {
    if (!transactionType) return;

    setConfiguration(null);
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseURL}/report/designs/field-configuration/${encodeURIComponent(transactionType)}`,
        { headers: authHeaders }
      );
      setConfiguration(response?.data?.data || null);
    } catch (error) {
      console.error("Failed to load report field configuration", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg ||
          "Failed to load report field configuration."
      );
    } finally {
      setLoading(false);
    }
  }, [authHeaders, transactionType]);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      loadConfiguration();
    }
  }, [open, loadConfiguration]);

  const updateVisibility = (section, fieldName, checked) => {
    setConfiguration(previous => {
      if (section.relatedIndex !== undefined) {
        return {
          ...previous,
          related: (previous?.related || []).map((relation, index) =>
            index === section.relatedIndex
              ? {
                  ...relation,
                  fields: (relation.fields || []).map(field =>
                    field.sFieldName === fieldName
                      ? { ...field, bShowInReport: checked }
                      : field
                  )
                }
              : relation
          )
        };
      }

      return {
        ...previous,
        [section.key]: {
          ...(previous?.[section.key] || emptySection),
          fields: (previous?.[section.key]?.fields || []).map(field =>
            field.sFieldName === fieldName
              ? { ...field, bShowInReport: checked }
              : field
          )
        }
      };
    });
  };

  const columnsFor = useMemo(
    () => section => [
      {
        field: "sCaption",
        headerName: "Caption",
        flex: 1,
        minWidth: 240,
        sortable: true
      },
      {
        field: "bShowInReport",
        headerName: "Show in Report",
        width: 160,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: params => (
          <Checkbox
            checked={Boolean(params.row.bShowInReport)}
            onChange={event =>
              updateVisibility(
                section,
                params.row.sFieldName,
                event.target.checked
              )
            }
            inputProps={{
              "aria-label": `Show ${params.row.sCaption} in Report Designer`
            }}
          />
        )
      }
    ],
    []
  );

  const saveConfiguration = async () => {
    if (!configuration || saving) return;

    setSaving(true);
    try {
      const payload = baseSections.reduce((result, section) => {
        result[section.key] = (configuration?.[section.key]?.fields || []).map(
          field => ({
            sFieldName: field.sFieldName,
            bShowInReport: Boolean(field.bShowInReport)
          })
        );
        return result;
      }, {});
      payload.related = (configuration?.related || []).map(relation => ({
        tableName: relation.tableName,
        fields: (relation.fields || []).map(field => ({
          sFieldName: field.sFieldName,
          bShowInReport: Boolean(field.bShowInReport)
        }))
      }));

      const response = await axios.put(
        `${baseURL}/report/designs/field-configuration/${encodeURIComponent(transactionType)}`,
        payload,
        { headers: authHeaders }
      );

      await onSaved?.();
      toast.success(
        response?.data?.metadata?.msg || "Report field configuration saved."
      );
      onClose?.();
    } catch (error) {
      console.error("Failed to save report field configuration", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg ||
          "Failed to save report field configuration."
      );
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => {
    if (!saving) onClose?.();
  };

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Report Designer Fields
        <Typography variant="body2" color="text.secondary">
          Transaction Type: {transactionType || "-"}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        disabled={saving}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ minHeight: 520 }}>
        {loading ? (
          <Box sx={{ minHeight: 440, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Report field table configuration"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {sections.map(section => (
                <Tab key={section.key} label={section.label} />
              ))}
            </Tabs>

            {sections.map((section, index) => {
              const sectionData = section.relatedIndex !== undefined
                ? configuration?.related?.[section.relatedIndex] || emptySection
                : configuration?.[section.key] || emptySection;
              const rows = sectionData.fields.map(field => ({
                ...field,
                id: field.sFieldName
              }));

              return (
                <Box
                  key={section.key}
                  role="tabpanel"
                  hidden={activeTab !== index}
                  aria-label={`${section.label} report fields`}
                >
                  {activeTab === index && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {sectionData.tableName || "No table configured"}
                      </Typography>
                      {!sectionData.tableName ? (
                        <Alert severity="info">
                          This transaction does not define a {section.label.toLowerCase()} table.
                        </Alert>
                      ) : (
                        <Box sx={{ height: 390, width: "100%" }}>
                          <DataGrid
                            rows={rows}
                            columns={columnsFor(section)}
                            disableRowSelectionOnClick
                            hideFooter
                            density="compact"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={closeDialog} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveConfiguration}
          disabled={loading || saving || !configuration}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
